import { useTranslation } from "react-i18next";
import { Card, ErrorBox, Skeleton } from "../../components/ui";
import { useCriticalRoleMatrix, useManagementTrainees, useSuccessionMap } from "../../lib/api";
import { localeFor } from "../../lib/format";
import { SuccessionCandidateRow, coverageHeat } from "./shared";

/** Critical roles, the management-trainee cohort and the succession map (WP-23.1). */
export default function Succession({ company }: { company?: string }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const fmtDate = (s: string | null | undefined) => (s ? new Intl.DateTimeFormat(localeFor(lng), { dateStyle: "medium" }).format(new Date(`${s.slice(0, 10)}T00:00:00`)) : "–");
  const criticalMatrix = useCriticalRoleMatrix(company, true);
  const trainees = useManagementTrainees(company, true);
  const succession = useSuccessionMap(company, true);

  return (
    <div data-ld-succession>
      {/* critical role competency */}
      <Card className="mt-6 overflow-hidden">
        <div className="border-b border-brand-grey-light px-4 py-3">
          <h2 className="font-heading text-base font-semibold">{t("ld.criticalRoles.title")}</h2>
          <span className="text-xs text-brand-grey">{t("ld.criticalRoles.hint")}</span>
        </div>
        {criticalMatrix.isLoading ? <div className="p-4"><Skeleton className="h-16" /></div> : criticalMatrix.isError ? <div className="p-4"><ErrorBox error={criticalMatrix.error} onRetry={() => void criticalMatrix.refetch()} /></div> : (criticalMatrix.data?.rows.length ?? 0) === 0 ? (
          <div className="p-6 text-center text-sm text-brand-grey">{t("ld.criticalRoles.noData")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
              <tr><th className="px-4 py-2">{t("ld.criticalRoles.designation")}</th><th className="px-4 py-2 text-right">{t("ld.criticalRoles.employees")}</th><th className="px-4 py-2 text-right">{t("ld.criticalRoles.coverage")}</th></tr>
            </thead>
            <tbody>
              {criticalMatrix.data?.rows.map((r) => (
                <tr key={r.designation} className="border-t border-brand-grey-light">
                  <td className="px-4 py-2 font-semibold">{r.designation}</td>
                  <td className="px-4 py-2 text-right">{r.employees}</td>
                  <td className={`px-4 py-2 text-right font-semibold tabular-nums ${coverageHeat(r.avg_coverage_pct)}`}>{r.avg_coverage_pct} %</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      {/* management trainees */}
      <Card className="mt-6 overflow-hidden">
        <div className="border-b border-brand-grey-light px-4 py-3">
          <h2 className="font-heading text-base font-semibold">{t("ld.trainees.title")}</h2>
          <span className="text-xs text-brand-grey">{t("ld.trainees.hint")}</span>
        </div>
        {trainees.isLoading ? <div className="p-4"><Skeleton className="h-16" /></div> : trainees.isError ? <div className="p-4"><ErrorBox error={trainees.error} onRetry={() => void trainees.refetch()} /></div> : (trainees.data?.length ?? 0) === 0 ? (
          <div className="p-6 text-center text-sm text-brand-grey">{t("ld.trainees.noTrainees")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
              <tr><th className="px-4 py-2">{t("wf.search.employee")}</th><th className="px-4 py-2 text-right">{t("ld.trainees.events")}</th><th className="px-4 py-2">{t("ld.trainees.rotations")}</th></tr>
            </thead>
            <tbody>
              {trainees.data?.map((r) => (
                <tr key={r.employee} className="border-t border-brand-grey-light">
                  <td className="px-4 py-2"><div className="font-semibold">{r.employee_name}</div><div className="text-[11px] text-brand-grey">{r.designation} · {r.company}</div></td>
                  <td className="px-4 py-2 text-right text-xs tabular-nums">{r.attended_events}/{r.events_total}</td>
                  <td className="px-4 py-2 text-xs text-brand-grey">
                    {r.rotations.length === 0 ? t("ld.trainees.noRotations") : r.rotations.map((rot) => `${rot.department ?? "–"} → ${rot.designation ?? "–"} (${fmtDate(rot.from_date)})`).join("; ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      {/* succession mapping */}
      <Card className="mt-6 overflow-hidden">
        <div className="border-b border-brand-grey-light px-4 py-3">
          <h2 className="font-heading text-base font-semibold">{t("ld.succession.title")}</h2>
          <span className="text-xs text-brand-grey">{t("ld.succession.hint")}</span>
        </div>
        {succession.isLoading ? <div className="p-4"><Skeleton className="h-16" /></div> : succession.isError ? <div className="p-4"><ErrorBox error={succession.error} onRetry={() => void succession.refetch()} /></div> : (succession.data?.length ?? 0) === 0 ? (
          <div className="p-6 text-center text-sm text-brand-grey">{t("ld.criticalRoles.noData")}</div>
        ) : (
          <div className="divide-y divide-brand-grey-light">
            {succession.data?.map((role) => (
              <div key={role.designation} className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-heading text-sm font-semibold">{role.designation}</h3>
                  <div className="text-xs text-brand-grey">{t("ld.succession.holder")}: {role.holders.map((h) => h.employee_name).join(", ")}</div>
                </div>
                {role.candidates.length === 0 ? <p className="mt-2 text-xs text-brand-grey">{t("ld.succession.noCandidates")}</p> : (
                  <table className="mt-2 w-full text-sm">
                    <tbody>
                      {role.candidates.slice(0, 5).map((c) => <SuccessionCandidateRow key={c.name} c={c} />)}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
